import { useState, useEffect, type CSSProperties } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { LogOut } from "lucide-react";
import { SidebarProvider, SidebarTrigger, SidebarInset } from "@/components/ui/sidebar";
import AdminSidebar, { OWNER_ONLY_TABS, type AdminTabId } from "@/components/admin/AdminSidebar";
import BlogList from "@/components/admin/BlogList";
import AdminCalendar from "@/components/AdminCalendar";
import SeoTrafficTab from "@/components/admin/SeoTrafficTab";
import SeoAiPanel from "@/components/admin/SeoAiPanel";
import AiDiscoverabilityPanel from "@/components/admin/AiDiscoverabilityPanel";
import GscPerformanceTab from "@/components/admin/GscPerformanceTab";
import NotificationCenter from "@/components/admin/NotificationCenter";
import VisitorIntelligenceTab from "@/components/admin/VisitorIntelligenceTab";
import RevenuePipelineTab from "@/components/admin/RevenuePipelineTab";
import ExecutiveCommandCenter from "@/components/admin/ExecutiveCommandCenter";
import AssetManager from "@/components/admin/AssetManager";
import CaseStudiesManager from "@/components/admin/CaseStudiesManager";
import TestimonialsManager from "@/components/admin/TestimonialsManager";
import ContentOperations from "@/components/admin/contentOps/ContentOperations";
import AIOperations from "@/components/admin/aiOps/AIOperations";
import LeadIntelligenceTab from "@/components/admin/LeadIntelligenceTab";
import TasksFollowUpCenter from "@/components/admin/TasksFollowUpCenter";
import ErrorBoundary from "@/components/ErrorBoundary";

// EGS navy palette mapped onto sidebar tokens
const ADMIN_VARS: CSSProperties = {
  ["--sidebar-background" as string]: "217 78% 9%",       // #071A35-ish
  ["--sidebar-foreground" as string]: "217 20% 82%",
  ["--sidebar-primary" as string]: "42 55% 60%",
  ["--sidebar-primary-foreground" as string]: "217 78% 12%",
  ["--sidebar-accent" as string]: "217 60% 18%",
  ["--sidebar-accent-foreground" as string]: "42 55% 70%",
  ["--sidebar-border" as string]: "217 50% 20%",
  ["--sidebar-ring" as string]: "42 55% 55%",
};

const TAB_TITLES: Record<AdminTabId, { title: string; sub: string }> = {
  command:      { title: "Executive Dashboard", sub: "Real-time operations overview" },
  visitors:     { title: "Visitors",            sub: "Live visitor intelligence" },
  submissions:  { title: "Lead Intelligence",   sub: "Inbound contact submissions" },
  pipeline:     { title: "Pipeline & Revenue",  sub: "Deals, stages, forecast" },
  calendar:     { title: "Calendar & Bookings", sub: "Appointments and availability" },
  tasks:        { title: "Tasks",                sub: "Due today, overdue, and upcoming work" },
  followups:    { title: "Follow-Up Center",     sub: "Lead notifications and status updates" },
  seoai:        { title: "SEO & AI Search",     sub: "Generative search readiness" },
  aidiscover:   { title: "AI Discoverability",   sub: "How ChatGPT, Claude, Perplexity, and Gemini see your site" },
  gsc:          { title: "Search Performance",  sub: "Google Search Console" },
  seo:          { title: "Traffic Analytics",   sub: "On-site SEO and traffic" },
  contentops:   { title: "Content Studio",       sub: "Write, refine, and ship, operations on demand" },
  blog:         { title: "Blog",                sub: "Published articles and editor" },
  testimonials: { title: "Testimonials",        sub: "Client voice and social proof" },
  cases:        { title: "Case Studies",        sub: "Outcomes and proof assets" },
  assets:       { title: "Assets & Media",      sub: "Site imagery and founder media" },
  aiops:        { title: "AI Operations",       sub: "Model routing and usage logs" },
};

const AdminDashboard = () => {
  const { user, signOut, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState<AdminTabId>("command");

  // Apply dark theme at the document root while the admin is mounted so
  // portaled popovers (selects, dropdowns, tooltips) also get dark tokens.
  useEffect(() => {
    document.documentElement.classList.add("dark");
    return () => document.documentElement.classList.remove("dark");
  }, []);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { tab?: AdminTabId } | undefined;
      if (detail?.tab && (isSuperAdmin || !OWNER_ONLY_TABS.includes(detail.tab))) setTab(detail.tab);
    };
    window.addEventListener("admin:navigate", handler);
    return () => window.removeEventListener("admin:navigate", handler);
  }, [isSuperAdmin]);

  const allowed = isSuperAdmin || !OWNER_ONLY_TABS.includes(tab);

  const heading = TAB_TITLES[tab];

  return (
    <div className="dark min-h-screen" style={{ background: "hsl(217 78% 7%)" }}>
      <SidebarProvider style={ADMIN_VARS}>
        <div className="flex min-h-screen w-full">
          <AdminSidebar active={tab} onSelect={setTab} isOwner={isSuperAdmin} />

          <SidebarInset className="min-w-0 overflow-x-hidden" style={{ background: "hsl(217 78% 7%)" }}>
            {/* Top bar */}
            <header className="sticky top-0 z-20 flex items-center justify-between gap-3 border-b px-4 md:px-6 py-3 backdrop-blur"
              style={{ borderColor: "hsl(217 50% 18%)", background: "hsl(217 78% 9% / 0.85)" }}>
              <div className="flex items-center gap-3 min-w-0">
                <SidebarTrigger className="text-[hsl(217_18%_75%)] hover:text-[hsl(0_0%_95%)]" />
                <div className="min-w-0">
                  <h1 className="text-base md:text-lg font-semibold truncate" style={{ color: "hsl(0 0% 95%)" }}>
                    {heading.title}
                  </h1>
                  <p className="text-xs truncate" style={{ color: "hsl(217 18% 65%)" }}>{heading.sub}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="hidden md:inline text-xs px-2 py-1 rounded-full border"
                  style={{ borderColor: "hsl(217 50% 22%)", color: "hsl(217 18% 70%)" }}>
                  {user?.email}
                </span>
                <NotificationCenter />
                <Button variant="outline" size="sm" onClick={() => { signOut(); navigate("/"); }}
                  className="border" style={{ borderColor: "hsl(217 50% 24%)", color: "hsl(0 72% 70%)", background: "transparent" }}>
                  <LogOut size={14} className="mr-1" /> Sign Out
                </Button>
              </div>
            </header>

            <main className="px-4 md:px-8 py-6 md:py-8">
              <ErrorBoundary fallbackTitle="This module couldn't load">
                {!allowed ? <p style={{ color: "hsl(217 18% 75%)" }}>This section is available to the account owner only.</p> : <>
                {tab === "command"      && <ExecutiveCommandCenter />}
                {tab === "visitors"     && <VisitorIntelligenceTab />}
                {tab === "submissions"  && <LeadIntelligenceTab />}
                {tab === "pipeline"     && <RevenuePipelineTab />}
                {tab === "calendar"     && <AdminCalendar />}
                {tab === "tasks"        && <TasksFollowUpCenter defaultView="tasks" />}
                {tab === "followups"    && <TasksFollowUpCenter defaultView="notifications" />}
                {tab === "seoai"        && <SeoAiPanel />}
                {tab === "aidiscover"   && <AiDiscoverabilityPanel />}
                {tab === "gsc"          && <GscPerformanceTab />}
                {tab === "seo"          && <SeoTrafficTab />}
                {tab === "contentops"   && <ContentOperations />}
                {tab === "blog"         && <BlogList />}
                {tab === "testimonials" && <TestimonialsManager />}
                {tab === "cases"        && <CaseStudiesManager />}
                {tab === "assets"       && <AssetManager />}
                {tab === "aiops"        && <AIOperations />}
                </>}
              </ErrorBoundary>
            </main>
          </SidebarInset>
        </div>
      </SidebarProvider>
    </div>
  );
};

export default AdminDashboard;
